import { useCallback, useEffect, useState } from 'react';
import { inventarioApi, isOfflineError, cache } from '../services/inventarioApi';
import { offlineQueue } from '../services/offlineQueue';
import { toLocalISOString, formatearFechaLocal, normalizarFechaISO } from '../../../utils/horaLocal';

const ENTIDAD = 'arqueos';

export function useArqueoCaja() {
  const [arqueos, setArqueos] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchArqueos = useCallback(async () => {
    try {
      const data = await inventarioApi.getAll(ENTIDAD);
      setArqueos(data);
      cache.set(ENTIDAD, data);
    } catch (e) {
      if (isOfflineError(e)) setArqueos(cache.get(ENTIDAD) || []);
      else console.error('Error fetch arqueos', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchArqueos(); }, [fetchArqueos]);

  const guardarArqueo = async (nuevoArqueo) => {
    const { id, ...body } = nuevoArqueo;
    // Asegurar que localId esté presente para que el backend pueda referenciar el registro
    if (!body.id && id) body.id = id;
    try {
      const created = await inventarioApi.create(ENTIDAD, body);
      setArqueos(prev => {
        const updated = [...prev, created];
        cache.set(ENTIDAD, updated);
        return updated;
      });
      return created;
    } catch (e) {
      if (isOfflineError(e)) {
        const temp = { id: id || `temp_${Date.now()}`, ...body };
        setArqueos(prev => {
          const updated = [...prev, temp];
          cache.set(ENTIDAD, updated);
          return updated;
        });
        offlineQueue.enqueue({ method: 'POST', entidad: ENTIDAD, body });
        return temp;
      }
      return false;
    }
  };

  const actualizarArqueos = (nuevosArqueos) => {
    setArqueos(nuevosArqueos);
    cache.set(ENTIDAD, nuevosArqueos);
    return true;
  };

  const obtenerArqueoDelDía = (fecha = toLocalISOString()) => {
    const fechaObjetivo = formatearFechaLocal(normalizarFechaISO(fecha));
    return arqueos.find(a => {
      const fechaArqueo = formatearFechaLocal(normalizarFechaISO(a.fecha));
      return fechaArqueo === fechaObjetivo && !a.anulado;
    });
  };

  const verificarDíaBloqueado = (fecha = toLocalISOString()) => !!obtenerArqueoDelDía(fecha);

  const anularArqueo = async (id) => {
    try {
      await inventarioApi.patch(ENTIDAD, { id, anulado: true });
      setArqueos(prev => {
        const updated = prev.map(a => a.id === id ? { ...a, anulado: true } : a);
        cache.set(ENTIDAD, updated);
        return updated;
      });
      return true;
    } catch (e) {
      if (isOfflineError(e)) {
        setArqueos(prev => {
          const updated = prev.map(a => a.id === id ? { ...a, anulado: true } : a);
          cache.set(ENTIDAD, updated);
          return updated;
        });
        offlineQueue.enqueue({ method: 'PATCH', entidad: ENTIDAD, body: { id, anulado: true } });
        return true;
      }
      console.error('Error al anular arqueo', e);
      return false;
    }
  };

  const calcularArqueoCaja = ({ movimientosEfectivo, gastos, ajustesDiarios }) => {
    const ingresos = movimientosEfectivo
      .filter(m => m.tipo === 'VENTA' || (m.tipo === 'SALIDA' && m.subTipo === 'VENTA'))
      .reduce((a, b) => a + (b.cantidad * b.precio), 0);

    const compras = movimientosEfectivo
      .filter(m => m.tipo === 'ENTRADA')
      .reduce((a, b) => a + (b.cantidad * b.precio), 0);

    const totalGastos = (gastos || [])
      .filter(g => g.estado === 'ACTIVO' && g.metodoPago === 'EFECTIVO')
      .reduce((a, b) => a + b.monto, 0);

    const ajustes = (ajustesDiarios || []).reduce((suma, a) => {
      return suma + (a.tipo === 'SOBRANTE' ? a.monto : -a.monto);
    }, 0);

    const total = ingresos + compras - totalGastos + ajustes;

    return { ingresos, compras, gastos: totalGastos, ajustes, total };
  };

  return {
    arqueos,
    guardarArqueo,
    anularArqueo,
    actualizarArqueos,
    obtenerArqueoDelDía,
    verificarDíaBloqueado,
    calcularArqueoCaja,
    loading,
    refetch: fetchArqueos,
  };
}